import { useState } from "react";
import {
  Calendar,
  Star,
  FileText,
  PhoneOff,
  ListFilter,
  ChevronDown,
  X,
} from "lucide-react";

export default function FilterTabs({
  selectedDate,
  setSelectedDate,
  selectedStatuses,
  setSelectedStatuses,
  filterStarred,
  setFilterStarred,
  notesFilter,
  setNotesFilter,
  notConnectedFilter,
  setNotConnectedFilter,
}) {
  const [activeFilter, setActiveFilter] = useState(null);

  const [showDateModal, setShowDateModal] = useState(false);
  const [showStatusModal, setShowStatusModal] = useState(false);

  const enquiryStatuses = [
    "Pending",
    "In Progress",
    "Cancelled",
    "Converted",
    "Not Interested",
  ];

  const toggleStatus = (status) => {
    setSelectedStatuses((prev) =>
      prev.includes(status)
        ? prev.filter((s) => s !== status)
        : [...prev, status]
    );
  };

  // ⭐ determines if filter button should be active (blue)
  const isFilterActive = (item) => {
    if (item.label === "Date") return selectedDate !== "";
    if (item.label === "Starred") return filterStarred;
    if (item.label === "Notes") return notesFilter === "withNotes";
    if (item.label === "Not Connected") return notConnectedFilter === true;
    if (item.label === "Enquiry Status") return selectedStatuses.length > 0;
    return false;
  };

  const filters = [
    { id: 1, label: "Date", icon: <Calendar className="w-4 h-4" /> },
    {
      id: 2,
      label: "Starred",
      icon: <Star className="w-4 h-4" />,
      toggle: () => setFilterStarred(!filterStarred),
    },
    {
      id: 3,
      label: "Notes",
      icon: <FileText className="w-4 h-4" />,
      toggle: () =>
        setNotesFilter(notesFilter === "withNotes" ? "" : "withNotes"),
    },
    {
      id: 4,
      label: "Not Connected",
      icon: <PhoneOff className="w-4 h-4" />,
      toggle: () => setNotConnectedFilter(!notConnectedFilter),
    },
    { id: 5, label: "Enquiry Status", icon: <ListFilter className="w-4 h-4" /> },
  ];

  return (
    <>
      {/* FILTER TABS */}
      <div className="w-full bg-white border-b py-3 px-2 flex gap-3 overflow-x-auto text-xs select-none">
        {filters.map((item) => (
          <button
            key={item.id}
            className={`flex items-center gap-1 text-xs tracking-wide px-4 py-1.5 rounded-md border transition-all whitespace-nowrap 
              ${
                isFilterActive(item)
                  ? "bg-blue-600 text-white"
                  : "bg-gray-100 text-gray-700 hover:bg-gray-200"
              }`}
            onClick={() => {
              setActiveFilter(item.id);

              // Close other modals
              setShowDateModal(false);
              setShowStatusModal(false);

              if (item.label === "Date") {
                setShowDateModal(true);
              } else if (item.label === "Enquiry Status") {
                setShowStatusModal(true);
              } else if (item.toggle) {
                item.toggle();
              }
            }}
          >
            {item.icon}
            {item.label}
            {item.label === "Enquiry Status" && (
              <ChevronDown className="w-3 h-3 ml-1" />
            )}
          </button>
        ))}
      </div>

      {/* DATE FILTER MODAL */}
      {showDateModal && (
        <div className="fixed inset-0 bg-black/40 backdrop-blur-sm z-50 flex justify-center items-end">
          <div className="w-full max-w-lg bg-white rounded-t-xl shadow-xl px-5 py-4 text-sm min-h-[45vh]">
            <div className="flex justify-between items-center pb-3 border-b mb-4">
              <h3 className="font-semibold text-gray-900 text-lg">
                Filter by Date
              </h3>
              <button
                onClick={() => setShowDateModal(false)}
                className="text-gray-600 hover:text-black p-1"
              >
                <X size={20} />
              </button>
            </div>

            <div className="flex flex-col gap-3 text-gray-800 font-medium">
              {["today", "yesterday", "custom"].map((label) => (
                <label
                  key={label}
                  className="flex items-center gap-3 cursor-pointer"
                >
                  <input
                    type="radio"
                    name="dateFilter"
                    value={label}
                    checked={selectedDate === label}
                    onChange={() => setSelectedDate(label)}
                    className="w-5 h-5 accent-blue-600"
                  />
                  <span className="text-base capitalize">{label}</span>
                </label>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* STATUS MODAL */}
      {showStatusModal && (
        <div className="fixed inset-0 bg-black/40 backdrop-blur-sm z-50 flex justify-center items-end">
          <div className="w-full max-w-lg bg-white rounded-t-xl shadow-xl px-5 py-4 text-sm min-h-[40vh]">
            <div className="flex justify-between items-center pb-3 border-b mb-4">
              <h3 className="font-semibold text-gray-900 text-lg">
                Filter by Status
              </h3>
              <button
                onClick={() => setShowStatusModal(false)}
                className="text-gray-600 hover:text-black p-1"
              >
                <X size={20} />
              </button>
            </div>

            <div className="flex flex-col gap-3 text-gray-900 font-medium">
              {enquiryStatuses.map((status) => (
                <label
                  key={status}
                  className="flex items-center gap-3 cursor-pointer"
                >
                  <input
                    type="checkbox"
                    className="w-5 h-5 accent-blue-600"
                    checked={selectedStatuses.includes(status)}
                    onChange={() => toggleStatus(status)}
                  />
                  <span className="text-base">{status}</span>
                </label>
              ))}
            </div>
          </div>
        </div>
      )}
    </>
  );
}
